import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
    Settings, Check, RefreshCw, MessageSquareCode, Sparkles,
    UploadCloud, Image as ImageIcon, Copy, Trash2, Paperclip, FileImage,
    Download, FileUp,
} from "lucide-react";

export default function BotCustomizer({ API, token, onUnauthorized }) {
    const [formData, setFormData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showStatus, setShowStatus] = useState("idle"); // idle | success | error

    const [uploadedImages, setUploadedImages] = useState([]);
    const [isLoadingImages, setIsLoadingImages] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    // JSON import/export
    const [importStatus, setImportStatus] = useState(null); // { type: 'success'|'error', msg: string }
    const importFileRef = useRef(null);

    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    // Convert any returned URL (relative or absolute) to an absolute URL that uses our backend host
    const absUrl = (u) => {
        if (!u) return u;
        if (/^https?:\/\//.test(u)) return u;
        const base = process.env.REACT_APP_BACKEND_URL || "";
        return `${base}${u.startsWith("/") ? "" : "/"}${u}`;
    };

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const r = await axios.get(`${API}/bot/settings`);
            setFormData(r.data);
        } catch (e) {
            console.error("Error fetching settings:", e);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchImages = async () => {
        setIsLoadingImages(true);
        try {
            const r = await axios.get(`${API}/uploaded-images`, authHeaders);
            if (r.data?.success) {
                const imgs = (r.data.images || []).map((i) => ({ ...i, fullUrl: absUrl(i.url) }));
                setUploadedImages(imgs);
            }
        } catch (e) {
            if (e?.response?.status === 401) onUnauthorized && onUnauthorized();
            console.error("Error fetching uploaded images:", e);
        } finally {
            setIsLoadingImages(false);
        }
    };

    useEffect(() => {
        fetchSettings();
        fetchImages();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleImageUpload = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            alert("אנא בחר קובץ תמונה בלבד (PNG, JPG, JPEG, GIF, WEBP)");
            return;
        }
        setIsUploading(true);
        try {
            const reader = new FileReader();
            const base64String = await new Promise((resolve, reject) => {
                reader.onload = () => resolve(reader.result);
                reader.onerror = (err) => reject(err);
                reader.readAsDataURL(file);
            });
            const r = await axios.post(
                `${API}/upload-image`,
                { name: file.name, base64: base64String },
                authHeaders
            );
            if (r.data?.success) {
                await fetchImages();
            } else {
                alert("שגיאה בהעלאת התמונה: " + (r.data?.error || "שגיאה לא ידועה"));
            }
        } catch (err) {
            console.error("Upload error:", err);
            alert("שגיאה בתקשורת עם השרת בזמן העלאת התמונה.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
        else if (e.type === "dragleave") setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleImageUpload(e.dataTransfer.files[0]);
        }
    };

    const handleFileInputChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleImageUpload(e.target.files[0]);
        }
    };

    const copyToClipboard = (text, id) => {
        let ok = false;
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text);
                ok = true;
            }
        } catch (e) { /* noop */ }
        if (!ok) {
            try {
                const ta = document.createElement("textarea");
                ta.value = text;
                ta.style.position = "absolute";
                ta.style.left = "-9999px";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            } catch (e) { /* noop */ }
        }
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const handleDeleteImage = async (img) => {
        if (!window.confirm("האם אתה בטוח שברצונך למחוק תמונה זו מהגלריה?")) return;
        try {
            const r = await axios.delete(`${API}/delete-image`, {
                ...authHeaders,
                data: { url: img.url },
            });
            if (r.data?.success) {
                await fetchImages();
            } else {
                alert("שגיאה במחיקת התמונה: " + (r.data?.error || "שגיאה לא ידועה"));
            }
        } catch (err) {
            console.error("Delete error:", err);
            alert("שגיאה בתקשורת עם השרת בזמן מחיקת התמונה.");
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };
    const handleToggle = (key) => setFormData((prev) => ({ ...prev, [key]: !prev[key] }));

    // ---------- Export current settings as JSON ----------
    const handleExport = () => {
        try {
            const exportPayload = {
                schema: "al-ahliyya-bot-settings/v1",
                exportedAt: new Date().toISOString(),
                collegeNameHe: formData.collegeNameHe,
                data: { ...formData },
            };
            // Do not export runtime-only / server-side computed fields
            delete exportPayload.data.primaryColor;
            delete exportPayload.data.accentColor;
            delete exportPayload.data.botAvatar;

            const blob = new Blob(
                [JSON.stringify(exportPayload, null, 2)],
                { type: "application/json;charset=utf-8" }
            );
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const today = new Date().toISOString().slice(0, 10);
            a.download = `al-ahliyya-bot-${today}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            setImportStatus({ type: "success", msg: `הבוט יוצא בהצלחה: ${a.download}` });
            setTimeout(() => setImportStatus(null), 4000);
        } catch (e) {
            setImportStatus({ type: "error", msg: `שגיאה בייצוא: ${e.message}` });
        }
    };

    // ---------- Import bot settings from JSON ----------
    const handleImportClick = () => importFileRef.current?.click();

    const handleImportFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = ""; // reset so same file can be re-selected
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".json")) {
            setImportStatus({ type: "error", msg: "יש להעלות קובץ .json בלבד" });
            return;
        }
        try {
            const text = await file.text();
            const parsed = JSON.parse(text);
            // Accept both { schema, data } shape (our export format) OR a flat settings object
            const incoming = parsed && parsed.schema && parsed.data ? parsed.data : parsed;
            if (!incoming || typeof incoming !== "object" || Array.isArray(incoming)) {
                throw new Error("מבנה JSON לא תקין");
            }
            // Sanity-check at least one known field
            const knownKeys = [
                "collegeNameHe", "collegeNameAr", "botPersonaHe", "welcomeMessageHe",
                "customKnowledgeText", "isActive", "strictMode",
            ];
            const hasKnown = knownKeys.some((k) => k in incoming);
            if (!hasKnown) throw new Error("הקובץ לא מכיל שדות בוט מוכרים");

            const proceed = window.confirm(
                "האם להחיל את ההגדרות מהקובץ על הבוט הפעיל?\n\nהערכים הנוכחיים יוחלפו (אפשר לייצא לפני כן לגיבוי)."
            );
            if (!proceed) return;

            setIsSaving(true);
            setShowStatus("idle");
            // Strip fields the server rejects / recomputes
            const payload = { ...incoming };
            delete payload.primaryColor;
            delete payload.accentColor;
            delete payload.botAvatar;

            const r = await axios.put(`${API}/bot/settings`, payload, authHeaders);
            setFormData(r.data);
            setImportStatus({ type: "success", msg: `הבוט יובא בהצלחה מ-${file.name}` });
            setShowStatus("success");
            setTimeout(() => {
                setShowStatus("idle");
                setImportStatus(null);
            }, 4000);
        } catch (err) {
            console.error("Import failed:", err);
            if (err?.response?.status === 401) onUnauthorized && onUnauthorized();
            const msg = err?.response?.data?.detail || err.message || "שגיאה לא ידועה";
            setImportStatus({ type: "error", msg: `שגיאה בייבוא: ${msg}` });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setShowStatus("idle");
        try {
            // Send only the editable subset
            const payload = { ...formData };
            delete payload.primaryColor;
            delete payload.accentColor;
            delete payload.botAvatar;
            const r = await axios.put(`${API}/bot/settings`, payload, authHeaders);
            setFormData(r.data);
            setShowStatus("success");
            setTimeout(() => setShowStatus("idle"), 3000);
        } catch (err) {
            console.error(err);
            if (err?.response?.status === 401) onUnauthorized && onUnauthorized();
            setShowStatus("error");
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading || !formData) {
        return (
            <div className="flex items-center justify-center py-20 text-[#475569]">
                <RefreshCw className="w-5 h-5 animate-spin me-2" />
                טוען הגדרות...
            </div>
        );
    }

    return (
        <div id="bot-customizer" className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-sm text-right fade-up" data-testid="bot-customizer">
            <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-4 mb-6">
                <div className="flex items-center gap-2 bg-emerald-50 text-emerald-800 px-3 py-1 rounded-full text-xs font-semibold">
                    <Sparkles className="w-3 h-3 text-emerald-600 animate-pulse" />
                    <span>מערכת מכוילת AI</span>
                </div>
                <h2 className="text-xl font-bold font-serif flex items-center justify-end gap-2 text-[#1A365D]">
                    הגדרות ואיפיון הבוט
                    <Settings className="w-5 h-5 text-[#475569]" />
                </h2>
            </div>

            {/* --- Import / Export bar --- */}
            <div className="mb-5 rounded-xl border border-[#E2E8F0] bg-gradient-to-l from-[#FFF8F0] to-white p-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3" dir="rtl">
                <div className="text-[13px] text-[#475569] leading-snug flex-1">
                    <div className="font-semibold text-[#0B1F3D] mb-0.5">גיבוי וייבוא של תצורת הבוט</div>
                    <div className="text-xs">ייצא את כל ההגדרות (persona · FAQ · KB · הודעות ברוכים הבאים) לקובץ JSON, או ייבא קובץ שיוצא בעבר.</div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={handleExport}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#1A365D] bg-white text-[#1A365D] hover:bg-[#F1F5F9] transition-colors text-sm font-semibold whitespace-nowrap"
                        data-testid="bot-export-json-btn"
                    >
                        <Download className="w-4 h-4" />
                        ייצוא JSON
                    </button>
                    <button
                        type="button"
                        onClick={handleImportClick}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#D4761F] text-white hover:bg-[#B36318] transition-colors text-sm font-semibold whitespace-nowrap"
                        data-testid="bot-import-json-btn"
                    >
                        <FileUp className="w-4 h-4" />
                        ייבוא JSON
                    </button>
                    <input
                        ref={importFileRef}
                        type="file"
                        accept="application/json,.json"
                        onChange={handleImportFile}
                        className="hidden"
                        data-testid="bot-import-file-input"
                    />
                </div>
            </div>
            {importStatus && (
                <div
                    className={`mb-4 rounded-lg px-4 py-2 text-sm font-medium ${
                        importStatus.type === "success"
                            ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                            : "bg-red-50 text-red-800 border border-red-200"
                    }`}
                    dir="rtl"
                    data-testid="bot-import-status"
                >
                    {importStatus.msg}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
                {/* State Toggles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ToggleRow
                        checked={!!formData.isActive}
                        onChange={() => handleToggle("isActive")}
                        title="סטטוס בוט פעיל"
                        sub="הפעלה או כיבוי של המענה האוטומטי"
                        testId="toggle-active"
                    />
                    <ToggleRow
                        checked={formData.strictMode ?? true}
                        onChange={() => handleToggle("strictMode")}
                        title="הגבלת תשובות למידע שהוזן קשיח"
                        sub="חסימת שאלות כלליות ומניעת הזיות (Strict Grounding)"
                        gold
                        testId="toggle-strict"
                    />
                </div>

                {/* Media library */}
                <div className="bg-[#F8F9FA] rounded-xl p-4 border border-[#E2E8F0] space-y-4 text-right">
                    <div className="flex items-center justify-between border-b border-[#E2E8F0] pb-2.5">
                        <span className="text-[11px] text-emerald-700 bg-emerald-100/70 px-2.5 py-0.5 rounded-full font-bold">
                            העלאת מדיה ישירה לשרת
                        </span>
                        <div className="flex items-center gap-1.5">
                            <span className="text-sm font-bold text-[#1A365D]">מאגר תמונות ומדיה לבוט (גרור ושחרר)</span>
                            <ImageIcon className="w-4 h-4 text-[#475569]" />
                        </div>
                    </div>

                    <p className="text-xs text-[#475569] leading-relaxed">
                        <b>כיצד להציג תמונה בשיחת הבוט?</b> גרור לכאן קובץ תמונה, העתק את <b>קוד התמונה</b> או הקישור בלחיצה אחת, והדבק בכל מקום בשדות ה-FAQ או המידע הכללי של המכללה. הבוט יציג את התמונה ישירות בבועה.
                    </p>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                        {/* Drag & Drop Area */}
                        <div className="lg:col-span-5">
                            <div
                                onDragEnter={handleDrag}
                                onDragOver={handleDrag}
                                onDragLeave={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                                className={`relative border-2 border-dashed rounded-xl p-6 transition-all cursor-pointer flex flex-col items-center justify-center text-center h-44 ${
                                    dragActive ? "border-emerald-500 bg-emerald-50/60" : "border-[#CBD5E1] hover:border-[#D4AF37] bg-white"
                                }`}
                                data-testid="image-dropzone"
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileInputChange}
                                    className="hidden"
                                    data-testid="image-file-input"
                                />
                                {isUploading ? (
                                    <div className="flex flex-col items-center gap-2 pointer-events-none">
                                        <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
                                        <span className="text-xs font-bold text-emerald-800">מעלה תמונה ומקודד...</span>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center gap-2 pointer-events-none">
                                        <UploadCloud className="w-9 h-9 text-[#94A3B8]" />
                                        <span className="text-xs font-bold text-[#1A365D]">לחץ לבחירה או גרור תמונה לכאן</span>
                                        <span className="text-[10px] text-[#94A3B8]">PNG, JPG, WEBP, GIF (עד 15MB)</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Gallery */}
                        <div className="lg:col-span-7 flex flex-col justify-between border border-[#E2E8F0] rounded-xl bg-white p-3 h-44">
                            <div className="overflow-y-auto pr-1 flex-1 space-y-2">
                                {isLoadingImages ? (
                                    <div className="flex items-center justify-center h-full gap-2 text-xs text-[#94A3B8]">
                                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                        <span>טוען תמונות...</span>
                                    </div>
                                ) : uploadedImages.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full text-[#94A3B8] gap-1.5">
                                        <FileImage className="w-7 h-7 text-[#CBD5E1]" />
                                        <span className="text-[11px]">אין עדיין תמונות במאגר. גרור קובץ להעלאה!</span>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {uploadedImages.map((img) => (
                                            <div key={img.url} className="group relative border border-[#E2E8F0] rounded-lg p-1 bg-[#F8F9FA] hover:bg-[#F1F5F9] transition" data-testid={`gallery-item-${img.id}`}>
                                                <div className="h-16 w-full rounded-md overflow-hidden bg-[#0F172A] border border-[#E2E8F0] flex items-center justify-center">
                                                    <img
                                                        src={img.fullUrl}
                                                        alt={img.name}
                                                        className="h-full w-full object-cover group-hover:scale-105 transition duration-300"
                                                        referrerPolicy="no-referrer"
                                                    />
                                                </div>
                                                <div className="mt-1 text-[9px] text-[#475569] truncate text-right px-0.5" title={img.name}>
                                                    {img.name}
                                                </div>
                                                <div className="absolute inset-0 bg-[#0F172A]/85 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5 p-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => copyToClipboard(`![${img.name.split('.')[0]}](${img.fullUrl})`, img.url + "-tag")}
                                                        className="w-full py-0.5 px-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[9px] font-bold flex items-center justify-center gap-0.5 transition"
                                                        data-testid={`copy-tag-${img.id}`}
                                                    >
                                                        {copiedId === img.url + "-tag" ? (
                                                            <><Check className="w-2.5 h-2.5" /> הועתק!</>
                                                        ) : (
                                                            <><Copy className="w-2.5 h-2.5" /> קוד תמונה</>
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => copyToClipboard(img.fullUrl, img.url + "-url")}
                                                        className="w-full py-0.5 px-1 bg-[#1A365D] hover:bg-[#0B2046] text-white rounded text-[9px] font-bold flex items-center justify-center gap-0.5 transition"
                                                        data-testid={`copy-url-${img.id}`}
                                                    >
                                                        {copiedId === img.url + "-url" ? (
                                                            <><Check className="w-2.5 h-2.5" /> הועתק!</>
                                                        ) : (
                                                            <><Paperclip className="w-2.5 h-2.5" /> העתק קישור</>
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteImage(img)}
                                                        className="w-full py-0.5 px-1 bg-rose-600 hover:bg-rose-700 text-white rounded text-[9px] font-bold flex items-center justify-center gap-0.5 transition"
                                                        data-testid={`delete-img-${img.id}`}
                                                    >
                                                        <Trash2 className="w-2.5 h-2.5" /> מחק תמונה
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="text-[9px] text-[#94A3B8] text-right border-t border-[#F1F5F9] pt-1.5 mt-2 flex justify-between items-center">
                                <span>* קוד תמונה: <code className="bg-[#F1F5F9] text-rose-600 px-1 py-0.5 rounded font-mono">{"![תיאור](קישור)"}</code></span>
                                <span className="font-semibold text-[#475569]">סה&quot;כ: {uploadedImages.length} תמונות</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Names */}
                <FieldPair
                    labelHe="שם המכללה בעברית" nameHe="collegeNameHe"
                    labelAr="اسم الكلية بالعربية" nameAr="collegeNameAr"
                    formData={formData} onChange={handleChange} required
                />

                {/* Locations */}
                <FieldPair
                    labelHe="סניפים ומיקום הלימודים (עברית)" nameHe="locationHe"
                    labelAr="المواقع والشركاء (العربية)" nameAr="locationAr"
                    formData={formData} onChange={handleChange}
                />

                {/* Voiceflow integration */}
                <div className="bg-blue-50/50 rounded-2xl p-5 border border-blue-100/70 space-y-3">
                    <div className="border-b border-blue-200/50 pb-2 flex items-center justify-between">
                        <span className="text-[10px] bg-blue-600 text-white font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                            Voiceflow Integration
                        </span>
                        <h3 className="text-sm font-bold text-blue-900 font-serif">
                            כתובת שיתוף/צ&apos;אט של סוכן הבינה המלאכותית (Voiceflow URL)
                        </h3>
                    </div>
                    <p className="text-xs text-blue-800 leading-relaxed">
                        הזן כאן את קישור השיתוף הציבורי של סוכן ה-Voiceflow שלכם. אם תשאיר שדה זה <b>ריק</b>, המערכת תשתמש בבוט המובנה.
                    </p>
                    <input
                        type="url"
                        name="voiceflowUrl"
                        value={formData.voiceflowUrl || ""}
                        onChange={handleChange}
                        className="w-full px-3.5 py-2.5 border border-blue-200 rounded-xl text-sm font-mono bg-white focus:border-blue-400 outline-none"
                        style={{ direction: "ltr" }}
                        placeholder="https://creator.voiceflow.com/prototype/..."
                        data-testid="voiceflow-url-input"
                    />
                </div>

                {/* Persona instructions */}
                <FieldArea
                    label={<span className="flex items-center justify-end gap-1"><span>הנחיות לפעולת הבוט (ערבית)</span><MessageSquareCode className="w-3.5 h-3.5" /></span>}
                    name="botPersonaAr"
                    placeholder="تعليمات الذكاء الاصطناعي للبوت..."
                    formData={formData} onChange={handleChange} rows={3}
                />
                <FieldArea
                    label="הנחיות לפעולת הבוט (עברית)"
                    name="botPersonaHe"
                    placeholder="הנחיות AI של הבוט בעברית..."
                    formData={formData} onChange={handleChange} rows={3}
                />

                {/* Welcome messages */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FieldArea label="הודעת פתיחה אוטומטית (עברית)" name="welcomeMessageHe" formData={formData} onChange={handleChange} rows={6} />
                    <FieldArea label="رسالة الترحيب التلقائية (العربية)" name="welcomeMessageAr" formData={formData} onChange={handleChange} rows={6} />
                </div>

                {/* Custom Knowledge Base */}
                <div className="bg-amber-50/60 rounded-2xl p-5 border border-amber-200/70 space-y-3">
                    <div className="border-b border-amber-200/60 pb-2 flex items-center justify-between">
                        <span className="text-[10px] bg-amber-600 text-white font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                            RAG · Source of Truth
                        </span>
                        <h3 className="text-sm font-bold text-amber-900 font-serif">
                            מסמך מקור הידע הבלעדי של הבוט (Knowledge Base)
                        </h3>
                    </div>
                    <p className="text-xs text-amber-800 leading-relaxed">
                        הדבק כאן את כל הידע שברצונך שהבוט יסתמך עליו (פרוטוקולים, שעות לימוד, תעריפים, תנאי קבלה).
                    </p>
                    <textarea
                        name="customKnowledgeText"
                        rows={6}
                        value={formData.customKnowledgeText || ""}
                        onChange={handleChange}
                        className="w-full px-3.5 py-2 border border-amber-200 rounded-xl text-sm text-right leading-relaxed bg-white focus:border-amber-400 outline-none"
                        placeholder="רשום או הדבק את המידע הרשמי שלכם..."
                        data-testid="custom-knowledge-textarea"
                    />
                </div>

                {/* FAQ Section */}
                <div className="bg-[#F8F9FA] rounded-2xl p-5 border border-[#E2E8F0] space-y-4">
                    <div className="border-b border-[#E2E8F0] pb-2 flex items-center justify-between">
                        <span className="text-[10px] bg-[#0F172A] text-[#D4AF37] font-semibold px-2 py-0.5 rounded-full font-mono">FAQ CONFIG</span>
                        <h3 className="text-sm font-bold text-[#1A365D] font-serif">תשובות אוטומטיות לשאלות נפוצות</h3>
                    </div>
                    <p className="text-xs text-[#475569]">
                        הזן את התשובות שיעשה בהן הבוט שימוש (הן בכפתורים המהירים והן בשאלות חופשיות):
                    </p>

                    {FAQ_ROWS.map((row, idx) => (
                        <FaqRow
                            key={row.key}
                            num={idx + 1}
                            icon={row.icon}
                            titleHe={row.titleHe}
                            titleAr={row.titleAr}
                            heName={`faq${row.key}He`}
                            arName={`faq${row.key}Ar`}
                            formData={formData}
                            onChange={handleChange}
                            multiline={row.multiline}
                        />
                    ))}
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-[#F1F5F9]">
                    <div>
                        {showStatus === "success" && (
                            <span className="text-emerald-600 text-xs font-bold flex items-center gap-1">
                                <Check className="w-4 h-4" /> הגדרות נשמרו בהצלחה
                            </span>
                        )}
                        {showStatus === "error" && (
                            <span className="text-rose-500 text-xs font-bold">שגיאה בתקשורת עם השרת</span>
                        )}
                    </div>
                    <button
                        type="submit"
                        disabled={isSaving}
                        className="btn-royal"
                        data-testid="bot-settings-save"
                    >
                        {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        שמור שינויים
                    </button>
                </div>
            </form>
        </div>
    );
}

// ============= Helper Subcomponents =============
function ToggleRow({ checked, onChange, title, sub, gold, testId }) {
    return (
        <div className={`flex items-center justify-between p-3.5 rounded-xl border ${gold ? "bg-emerald-50/40 border-emerald-100" : "bg-[#F8F9FA] border-[#E2E8F0]"}`}>
            <label className="relative inline-flex items-center cursor-pointer select-none">
                <input type="checkbox" checked={checked} onChange={onChange} className="sr-only peer" data-testid={testId} />
                <div className="w-11 h-6 bg-[#CBD5E1] rounded-full peer peer-checked:bg-emerald-600 transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
            </label>
            <div className="text-right">
                <span className="block text-sm font-bold text-[#0F172A]">{title}</span>
                <span className="text-[11px] text-[#475569]">{sub}</span>
            </div>
        </div>
    );
}

function FieldPair({ labelHe, nameHe, labelAr, nameAr, formData, onChange, required }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
                <label className="block text-xs font-bold text-[#1A365D] uppercase tracking-wider">{labelHe}</label>
                <input
                    type="text" name={nameHe} value={formData[nameHe] || ""} onChange={onChange}
                    className="input-royal !py-2 text-right" required={required}
                    data-testid={`field-${nameHe}`}
                />
            </div>
            <div className="space-y-1">
                <label className="block text-xs font-bold text-[#1A365D] uppercase tracking-wider">{labelAr}</label>
                <input
                    type="text" name={nameAr} value={formData[nameAr] || ""} onChange={onChange}
                    className="input-royal !py-2 text-right" required={required}
                    data-testid={`field-${nameAr}`}
                />
            </div>
        </div>
    );
}

function FieldArea({ label, name, placeholder, formData, onChange, rows = 4 }) {
    return (
        <div className="space-y-1">
            <label className="block text-xs font-bold text-[#1A365D] uppercase tracking-wider">{label}</label>
            <textarea
                name={name}
                rows={rows}
                value={formData[name] || ""}
                onChange={onChange}
                className="input-royal text-right leading-relaxed"
                placeholder={placeholder}
                data-testid={`field-${name}`}
            />
        </div>
    );
}

function FaqRow({ num, icon, titleHe, titleAr, heName, arName, formData, onChange, multiline }) {
    return (
        <div className="bg-white p-3 rounded-xl border border-[#E2E8F0] space-y-2">
            <span className="font-bold text-xs text-[#1A365D] block">
                {num}. {icon} {titleHe} / <span className="text-[#475569]">{titleAr}</span>
            </span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pb-1">
                <div className="space-y-1">
                    <span className="block text-[10px] font-bold text-[#94A3B8]">תשובה בעברית:</span>
                    {multiline ? (
                        <textarea rows={2} name={heName} value={formData[heName] || ""} onChange={onChange} className="input-royal !py-1.5 text-xs" data-testid={`field-${heName}`} />
                    ) : (
                        <input type="text" name={heName} value={formData[heName] || ""} onChange={onChange} className="input-royal !py-1.5 text-xs" data-testid={`field-${heName}`} />
                    )}
                </div>
                <div className="space-y-1">
                    <span className="block text-[10px] font-bold text-[#94A3B8]">الرد بالعربية:</span>
                    {multiline ? (
                        <textarea rows={2} name={arName} value={formData[arName] || ""} onChange={onChange} className="input-royal !py-1.5 text-xs" data-testid={`field-${arName}`} />
                    ) : (
                        <input type="text" name={arName} value={formData[arName] || ""} onChange={onChange} className="input-royal !py-1.5 text-xs" data-testid={`field-${arName}`} />
                    )}
                </div>
            </div>
        </div>
    );
}

const FAQ_ROWS = [
    { key: "Location", icon: "📍", titleHe: "איפה הלימודים", titleAr: "وين التعليم" },
    { key: "Days", icon: "📅", titleHe: "כמה ימי לימוד בשבוע", titleAr: "كم يوم التعليم" },
    { key: "Years", icon: "⏳", titleHe: "כמה שנים לומדים", titleAr: "كم سنة التعليم" },
    { key: "Recommend", icon: "🎓", titleHe: "מה מומלץ ללמוד", titleAr: "شو حاب تدرس" },
    { key: "Tuition", icon: "💵", titleHe: "שכר לימוד", titleAr: "الأقساط الدراسية" },
    { key: "Format", icon: "💻", titleHe: "פרונטלי או זום", titleAr: "وجاهي أم زوم" },
    { key: "Language", icon: "🗣️", titleHe: "שפת הלימוד", titleAr: "لغة التعليم" },
    { key: "NextSteps", icon: "🚀", titleHe: "המשך תואר והרשמה", titleAr: "الخيار الأكاديمي والتسجيل", multiline: true },
];
